import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import HistoryIcon from '@material-ui/icons/History'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { userInfoState } from '../../recoil/atoms'
import { beginImpersonation } from '../../api/impersonation'
import { api } from '../../api/client'
import Layout from '../../components/layout/Layout'
import { logoSrc } from '../../api/logoSrc'
import { toFarsiDigits } from '../../assets/utils'
import RowActions from '../../components/common/RowActions'

// شمارهٔ صفحه‌ها: اعدادِ نزدیک به صفحهٔ جاری + «...» برای وسط
const pageItems = (cur, total) => {
  const out = []
  if (total <= 7) {
    for (let i = 1; i <= total; i++) out.push(i)
    return out
  }
  out.push(1)
  if (cur > 3) out.push('...')
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) out.push(i)
  if (cur < total - 2) out.push('...')
  out.push(total)
  return out
}

const thStyle = {
  backgroundColor: '#1a237e',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: 12.5,
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 3,
}

const money = (n) => toFarsiDigits(Number(n || 0).toLocaleString('en-US'))

const StatCard = ({ label, value, sub, gradient, shadow }) => (
  <div style={{
    borderRadius: 14, padding: '18px 20px', color: '#fff', position: 'relative',
    overflow: 'hidden', background: gradient, boxShadow: shadow,
  }}>
    <div style={{ fontSize: 12.5, opacity: 0.88 }}>{label}</div>
    <div style={{ fontSize: 23, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    {sub ? <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 2 }}>{sub}</div> : null}
  </div>
)

const STAT_GRID = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 14, marginBottom: 16,
}

const useStyles = makeStyles(() => ({
  periods: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  pill: {
    padding: '5px 14px', borderRadius: 999, border: '1px solid #d7dde7', fontSize: 12.5,
    fontWeight: 700, color: '#455a64', background: '#fff', cursor: 'pointer',
  },
  pillOn: { background: '#1a237e', borderColor: '#1a237e', color: '#fff' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  count: { fontSize: 12.5, color: '#607d8b', fontWeight: 700 },
  wrap: {
    transition: 'opacity .15s ease',
    maxHeight: '68vh',
    overflow: 'auto',
    border: '1px solid #e6e9f6',
    borderRadius: 10,
  },
  dim: { opacity: 0.45 },
  pgnNum: {
    minWidth: 30, height: 30, borderRadius: 8, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, background: '#fff', color: '#3949ab',
    border: '1px solid #c5cae9', padding: '0 8px',
  },
  pgnOn: { background: '#1a237e', color: '#fff', borderColor: '#1a237e' },
  pgnArrow: {
    minWidth: 32, height: 30, borderRadius: 8, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, lineHeight: 1, background: '#eef1ff', color: '#1a237e',
    border: '1px solid #c5cae9', userSelect: 'none', paddingBottom: 4,
  },
  pgnOff: { opacity: .35, cursor: 'default', pointerEvents: 'none' },
  pgnDots: { fontSize: 14, color: '#9aa1b9', padding: '0 2px', userSelect: 'none' },
  row: { cursor: 'pointer' },
}))

export default function ContractorsPayments({ embedded = false, period: periodProp, search: searchProp }) {
  const classes = useStyles()
  const userInfo = useRecoilValue(userInfoState)
  const setUserInfo = useSetRecoilState(userInfoState)
  const [q, setQ] = useState(searchProp || '')
  const [period, setPeriod] = useState(periodProp || '1y')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('active')   // active | expired | all
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [report, setReport] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const first = useRef(true)

  // هم‌گام‌سازی با فیلترهای صفحهٔ میزبان (وقتی embedded است)
  useEffect(() => {
    if (periodProp && periodProp !== period) { setPeriod(periodProp); setPage(1) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodProp])
  useEffect(() => {
    if (searchProp !== undefined && searchProp !== q) { setQ(searchProp); setPage(1) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchProp])

  const load = useCallback(async (query, pg) => {
    setLoading(true)
    setErr('')
    try {
      const url = `users/admin/payment-history/?period=${period}&page=${pg}`
        + `&count=50&status=${status}&q=${encodeURIComponent(query || '')}`
      const res = await api.get(url)
      setData(res.data)
    } catch (e) {
      setErr('دریافت سابقهٔ پرداخت ناموفق بود')
    } finally {
      setLoading(false)
    }
  }, [period, status])

  // جست‌وجوی لحظه‌ای با «وقفهٔ تایپ» (debounce)
  useEffect(() => {
    if (first.current) {
      first.current = false
      load(q, page)
      return undefined
    }
    const t = setTimeout(() => load(q, page), 350)
    return () => clearTimeout(t)
  }, [q, period, page, load]) // eslint-disable-line react-hooks/exhaustive-deps

  // ورود به حسابِ پیمانکار (Impersonation) — مثل صفحهٔ پرداخت‌ها
  const loginHandler = (id) => {
    beginImpersonation(userInfo)
    api.post('users/login/' + id + '?by=user').then((result) => {
      if (result.status === 200) {
        setUserInfo(result.data.user)
        localStorage.setItem('access', result.data.tokens.access)
        if (result.data.tokens.refresh) {
          localStorage.setItem('refresh', result.data.tokens.refresh)
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + result.data.tokens.access
      }
    }).catch(() => setErr('ورود به حساب ناموفق بود'))
  }

  const openReport = async (id) => {
    setReport({ loading: true })
    setReportLoading(true)
    try {
      const res = await api.get(`users/admin/payment-history/?user=${id}`)
      setReport(res.data.user)
    } catch (e) {
      setErr('دریافت گزارش ناموفق بود')
      setReport(null)
    } finally {
      setReportLoading(false)
    }
  }

  const stats = (data && data.stats) || {}
  const items = (data && data.items) || []
  const periods = (data && data.periods) || []

  const body = (
    <>
      <Box p={2} style={{ fontFamily: 'Vazir' }}>
        <Typography variant="body2" style={{ color: '#607d8b', marginBottom: 16 }}>
          همهٔ پیمانکارانی که پرداختی داشته‌اند (شاملِ اشتراک‌های منقضی) — مرتب بر اساس آخرین پرداخت. بازهٔ بالا فقط روی آمار اثر دارد.
        </Typography>

        {err ? (
          <Typography style={{ color: '#c62828', marginBottom: 12, fontSize: 13 }}>{err}</Typography>
        ) : null}

        {!embedded && (
        <div className={classes.periods}>
          <span style={{ fontSize: 12.5, color: '#607d8b', fontWeight: 700 }}>بازهٔ آمار:</span>
          {periods.map((p) => (
            <span
              key={p.code}
              className={`${classes.pill} ${(data && data.period === p.code) ? classes.pillOn : ''}`}
              onClick={() => { setPeriod(p.code); setPage(1) }}
            >
              {p.title}
            </span>
          ))}
        </div>
        )}

        <div style={STAT_GRID}>
          <StatCard
            label={`جمع پرداخت‌ها — ${(data && data.period_label) || ''}`}
            value={`${money(stats.sum)} ریال`}
            sub={data && data.since ? `از ${data.since} تا امروز` : 'کل تاریخ'}
            gradient="linear-gradient(135deg,#0b3d3a,#0f766e 60%,#0d9488)"
            shadow="0 16px 30px -20px rgba(13,148,136,.95)"
          />
          <StatCard
            label={`تعداد پرداخت‌ها — ${(data && data.period_label) || ''}`}
            value={toFarsiDigits(stats.count || 0)}
            gradient="linear-gradient(135deg,#283593,#3949ab 60%,#5c6bc0)"
            shadow="0 16px 30px -20px rgba(57,73,171,.95)"
          />
          <StatCard
            label="پرداخت‌های نیازمند بررسی"
            value={toFarsiDigits(stats.unresolved || 0)}
            sub="تأییدشده در درگاه ولی ثبت‌نشده"
            gradient="linear-gradient(135deg,#b45309,#f59e0b 60%,#fbbf24)"
            shadow="0 16px 30px -20px rgba(245,158,11,.95)"
          />
        </div>

        <div className={classes.toolbar}>
          {!embedded && (
          <TextField
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1) }}
            placeholder="نام، موبایل یا شناسه ملی پیمانکار…"
            variant="outlined"
            size="small"
            style={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#90a4ae' }} />
                </InputAdornment>
              ),
            }}
          />
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { code: 'active', title: 'اشتراک فعال' },
              { code: 'expired', title: 'منقضی‌شده' },
              { code: 'all', title: 'همه' },
            ].map((t) => (
              <span
                key={t.code}
                className={`${classes.pill} ${status === t.code ? classes.pillOn : ''}`}
                onClick={() => { setStatus(t.code); setPage(1) }}
              >
                {t.title}
              </span>
            ))}
          </div>
          <span className={classes.count}>
            {toFarsiDigits((data && data.count) || 0)} پیمانکار
            {status === 'expired' ? ' منقضی‌شده' : (status === 'active' ? ' با اشتراک فعال' : '')}
            {(data && data.period_label) ? ` — ${data.period_label}` : ''}
          </span>
          {loading ? <CircularProgress size={18} /> : null}
        </div>

        <div className={`${classes.wrap} ${loading ? classes.dim : ''}`}>
          <TableContainer component={Paper} elevation={2} style={{ overflow: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'لوگو', 'ورود', 'پیمانکار', 'آخرین پرداخت', 'پایان اشتراک',
                    'جمع پرداخت (ریال)', 'موبایل', 'آخرین بازدید', 'روزهای مانده',
                    'سابقه', 'پیام'].map((h) => (
                    <TableCell key={h} align="center" style={thStyle}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" style={{ padding: 28, color: '#90a4ae' }}>
                      پیمانکاری یافت نشد
                    </TableCell>
                  </TableRow>
                )}
                {items.map((r, i) => (
                  <TableRow key={r.id} hover className={classes.row} onClick={() => openReport(r.id)}>
                    <TableCell align="center">{toFarsiDigits((page - 1) * 50 + i + 1)}</TableCell>
                    <TableCell align="center">
                      <img
                        src={logoSrc(`users/logo/${r.id}/`)}
                        alt=""
                        style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e3e6f2' }}
                        onError={(e) => { e.target.style.visibility = 'hidden' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ExitToAppIcon style={{ fontSize: 16 }} />}
                        onClick={(e) => { e.stopPropagation(); loginHandler(r.id) }}
                        style={{
                          background: '#1a237e', color: '#fff', fontWeight: 700,
                          textTransform: 'none', fontSize: 11.5, whiteSpace: 'nowrap',
                        }}
                      >
                        ورود
                      </Button>
                    </TableCell>
                    <TableCell style={{ fontWeight: 700 }}>{r.name}</TableCell>
                    <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                      {r.last ? (r.last_time ? `${r.last} — ${r.last_time}` : r.last) : '—'}
                    </TableCell>
                    <TableCell align="center">{r.sub_end || '—'}</TableCell>
                    <TableCell align="center" style={{ fontWeight: 800 }}>{money(r.total)}</TableCell>
                    <TableCell align="center" dir="ltr">{r.mobile || '—'}</TableCell>
                    <TableCell align="center" style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>
                      {r.last_seen ? `${r.last_seen}${r.last_seen_time ? ' — ' + r.last_seen_time : ''}` : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {r.days_left === null || r.days_left === undefined ? '—' : (
                        <span style={{
                          fontWeight: 800, fontSize: 12,
                          color: r.days_left < 0 ? '#c62828' : (r.days_left <= 30 ? '#b45309' : '#2e7d32'),
                          background: r.days_left < 0 ? '#ffebee' : (r.days_left <= 30 ? '#fff8e1' : '#e8f5e9'),
                          borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap',
                        }}>
                          {r.days_left < 0 ? 'منقضی' : `${toFarsiDigits(r.days_left)} روز`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<HistoryIcon style={{ fontSize: 16 }} />}
                        onClick={(e) => { e.stopPropagation(); openReport(r.id) }}
                        style={{
                          color: '#1a237e', borderColor: '#9fa8da', fontWeight: 700,
                          textTransform: 'none', fontSize: 11.5, whiteSpace: 'nowrap',
                        }}
                      >
                        سابقهٔ پرداخت
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <RowActions userId={r.id} mobile={r.mobile} name={r.name} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {data && data.pages > 1 && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}
            style={{ gap: 6, flexWrap: 'wrap' }} className="ap-pagination">
            <span className={`${classes.pgnArrow} ${page <= 1 ? classes.pgnOff : ''}`}
              onClick={() => page > 1 && setPage((p) => p - 1)}
              title="صفحهٔ قبلی">‹</span>
            {pageItems(page, data.pages).map((it, i) => (it === '...' ? (
              <span key={`e${i}`} className={classes.pgnDots}>…</span>
            ) : (
              <span
                key={it}
                className={`${classes.pgnNum} ${page === it ? classes.pgnOn : ''}`}
                onClick={() => setPage(it)}
              >
                {toFarsiDigits(it)}
              </span>
            )))}
            <span className={`${classes.pgnArrow} ${page >= data.pages ? classes.pgnOff : ''}`}
              onClick={() => page < data.pages && setPage((p) => p + 1)}
              title="صفحهٔ بعدی">›</span>
          </Box>
        )}
      </Box>

      <Dialog open={!!report} onClose={() => setReport(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ fontFamily: 'Vazir' }}>
            <Box flex={1} fontWeight={800}>
              {report && report.name ? `سابقهٔ پرداخت: ${report.name}` : 'در حال بارگذاری…'}
            </Box>
            <IconButton size="small" onClick={() => setReport(null)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ fontFamily: 'Vazir' }}>
          {reportLoading || !report || !report.pays ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <>
              <Box display="flex" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                <StatCard label="جمع کل پرداخت‌ها" value={`${money(report.total)} ریال`}
                  sub={`${toFarsiDigits(report.count)} پرداخت`}
                  gradient="linear-gradient(135deg,#0b3d3a,#0f766e 60%,#0d9488)"
                  shadow="0 16px 30px -20px rgba(13,148,136,.95)" />
                <StatCard label="پایان اشتراک" value={report.sub_end || '—'}
                  sub={report.active ? 'اشتراک فعال' : 'منقضی'}
                  gradient="linear-gradient(135deg,#283593,#3949ab 60%,#5c6bc0)"
                  shadow="0 16px 30px -20px rgba(57,73,171,.95)" />
              </Box>

              <Typography variant="body2" style={{ color: '#607d8b', marginBottom: 10 }}>
                موبایل: {report.mobile || '—'} — شناسه ملی: {report.national_code || '—'}
              </Typography>

              <Typography variant="subtitle2" style={{ fontWeight: 800, margin: '14px 0 8px' }}>
                پرداخت‌های ثبت‌شده
              </Typography>
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['تاریخ', 'ساعت', 'مبلغ (ریال)', 'سال', 'شمارهٔ پیگیری'].map((h) => (
                        <TableCell key={h} align="center" style={thStyle}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.pays.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell align="center">{p.date}</TableCell>
                        <TableCell align="center">{p.time}</TableCell>
                        <TableCell align="center" style={{ fontWeight: 800 }}>{money(p.amount)}</TableCell>
                        <TableCell align="center">{toFarsiDigits(p.years)}</TableCell>
                        <TableCell align="center" dir="ltr" style={{ fontSize: 11.5 }}>{p.reference || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {report.atts && report.atts.length > 0 && (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 800, margin: '16px 0 8px' }}>
                    تلاش‌های پرداخت
                  </Typography>
                  <TableContainer component={Paper} elevation={1}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['تاریخ', 'مبلغ (ریال)', 'درگاه', 'وضعیت', 'خطا'].map((h) => (
                            <TableCell key={h} align="center" style={thStyle}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.atts.map((a, i) => (
                          <TableRow key={i}>
                            <TableCell align="center">{a.date}</TableCell>
                            <TableCell align="center">{money(a.amount)}</TableCell>
                            <TableCell align="center">{a.source}</TableCell>
                            <TableCell align="center">{a.state}</TableCell>
                            <TableCell style={{ fontSize: 11.5, color: '#90a4ae' }}>{a.error || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReport(null)} color="primary">بستن</Button>
        </DialogActions>
      </Dialog>
    </>
  )
  return embedded ? body : <Layout title="سابقهٔ پرداخت پیمانکاران">{body}</Layout>
}
